import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';

import _e from 'common/localeUtil';
import { REVISION_STATUSES } from '../../../constants';
import { generateUrlFromLocalStorage } from '../../utils';
import ConfigureEbookIntroductionDialog from '../../../components/dialog/ConfigureEbookIntroductionDialog';

function useForceUpdate() {
	// eslint-disable-next-line no-unused-vars
	const [value, setValue] = useState(0); // integer state
	// eslint-disable-next-line no-plusplus
	return () => setValue(value => ++value); // update the state to force render
}

const CustomButton = ({ children, onClick, ...rest }) => {
	const ref = useRef();
	useEffect(() => {
		if (!onClick) {
			return () => {};
		}
		ref.current.addEventListener('click', onClick);
		ref.current.addEventListener('touchstart', onClick);

		return () => {
			ref.current.removeEventListener('click', onClick);
			ref.current.removeEventListener('touchstart', onClick);
		};
	}, [onClick]);

	return (
		<div {...rest} role="button" ref={ref}>
			{children}
		</div>
	);
};

CustomButton.propTypes = {
	onClick: PropTypes.func,
	children: PropTypes.any
};

const ConfigSectionPanel = ({
	configurable = false,
	layoutable = false,
	backgroundEditable = false,
	editor,
	colorChangeable,
	selectedComponent
}) => {
	const { el } = selectedComponent;
	const forceUpdate = useForceUpdate();

	const [showConfigurationEbookIntroDialog, setShowConfigurationEbookIntroDialog] = useState(
		false
	);

	const toggleShowConfigurationEbookIntroDialog = () => {
		setShowConfigurationEbookIntroDialog(!showConfigurationEbookIntroDialog);
	};

	const handleConfig = () => {
		editor.runCommand('openConfigurationEditor', {
			sectionId: `#${el.id}`
		});
	};
	const handleLayout = () => {
		editor.runCommand('openLayoutEditor', {
			sectionId: `#${el.id}`
		});
	};
	const handleDownloadSampleEbook = () => {
		const ebookUrl = localStorage.getItem('GM_EBOOK_FINAL_URL');
		console.log('ebookurl', ebookUrl);
		window.open(ebookUrl);
	};

	const { ebookintro_status: ebookIntroStatus } = window.GM_STATUS_DATA;
	const handleConfirmConfigureEbookIntro = () => {
		// console.log('handleConfirmConfigureEbookIntro', ebookIntroStatus);
		if (ebookIntroStatus?.status === REVISION_STATUSES.PUBLISHED) {
			const url = generateUrlFromLocalStorage(ebookIntroStatus?.template);
			// console.log('handleConfigureEbookIntro', url);
			window.open(url, 'GM_BRANDING_EBOOK_URL');
		} else {
			window.open(
				`${process.env.REACT_APP_BASE_API_URL}/branding/ebook`,
				'GM_BRANDING_EBOOK_URL'
			);
		}
		toggleShowConfigurationEbookIntroDialog();
	};
	const handleBackground = () => {
		const elWidth = el.offsetWidth;
		console.log('handleBackground', elWidth);
		let maxWidth;
		let maxHeight = el.offsetHeight;
		if (window.GM_BRANDING_TYPE_REPORT_EBOOK) {
			const withScale = 2;
			const cropRatio = 0.7069; // A4 size
			maxWidth = elWidth * withScale;
			maxHeight = Math.floor(maxWidth / cropRatio);

			// maxWidth = elWidth;
		} else {
			// Website templates
			maxWidth = 1910; // default full width
			if (elWidth < 1050) {
				// template9 width 1024
				maxWidth = 1024;
			} else if (elWidth < 1350) {
				// template6 width 1310
				maxWidth = 1790;
			}
		}

		const imageEditorOptions = {
			showImageEditor: true,
			maxWidth,
			maxHeight,
			cropRatio: maxWidth / maxHeight
		};
		// console.log('handleBackground', el.offsetWidth, el.offsetHeight);
		editor.runCommand('openChangeBackgroundDialog', {
			sectionId: `#${el.id}`,
			colorChangeable,
			...imageEditorOptions
		});
	};

	const handleLayoutSelected = useCallback(
		layout => {
			return () => {
				const wrapper = editor.getWrapper();
				const component = wrapper.find(`#${el.id}`)[0];
				console.log('handleLayoutSelected', component, `#${el.id}`, layout);
				const attributes = component.getAttributes();
				component.setAttributes({
					...attributes,
					'data-layout-col': layout
				});
				forceUpdate();

				window.gm_track(`Layout selected`, { elId: el.id, layout });
			};
		},
		[editor]
	);

	const { layoutCol, maxCol } = el.dataset;
	const renderDropdownContent = () => {
		return (
			<div className="dropdown-menu" id="layout-dropdown-menu" role="menu">
				<div className="dropdown-content">
					<div className="dropdown-item">
						<div className="columns">
							<div className="title">{_e('columns')}</div>
						</div>
						<div className="columns">
							{Array(parseInt(maxCol, 10))
								.fill(0)
								.map((_, i) => (
									<CustomButton
										// eslint-disable-next-line react/no-array-index-key
										key={`layout-${i}`}
										className={`column layout-option-${i +
											1} editor-layout-item ${
											i === layoutCol - 1 ? 'selected' : ''
										}`}
										onClick={handleLayoutSelected(i + 1)}
									>
										{i + 1}
									</CustomButton>
								))}
						</div>
					</div>
				</div>
			</div>
		);
	};

	// console.log('ConfigSectionPanel', { selectedComponent, elId: el.id });

	return (
		<>
			{el.id === 'section-ebook' && (
				<>
					<CustomButton
						className="editor-button configure-ebookintro-button"
						onClick={toggleShowConfigurationEbookIntroDialog}
					>
						{_e('configure_intro_page')}
					</CustomButton>
					<CustomButton
						className="editor-button download-sample-ebook-button"
						onClick={handleDownloadSampleEbook}
					>
						{_e('download_ebook')}
					</CustomButton>
					{showConfigurationEbookIntroDialog && (
						<ConfigureEbookIntroductionDialog
							open={showConfigurationEbookIntroDialog}
							handleClose={toggleShowConfigurationEbookIntroDialog}
							handleConfirmAction={handleConfirmConfigureEbookIntro}
						/>
					)}
				</>
			)}
			{configurable && (
				<CustomButton className="editor-button configure-button" onClick={handleConfig}>
					{_e('configure')}
				</CustomButton>
			)}
			{layoutable && (
				<div className="dropdown is-hoverable">
					<div className="dropdown-trigger">
						<CustomButton
							onClick={handleLayout}
							className="editor-button layout-button"
							aria-haspopup="true"
							aria-controls="layout-dropdown-menu"
						>
							{_e('layout')}
						</CustomButton>
					</div>
					{renderDropdownContent()}
				</div>
			)}
			{backgroundEditable && (
				<CustomButton
					className="editor-button background-button"
					onClick={handleBackground}
				>
					{_e('background')}
				</CustomButton>
			)}
		</>
	);
};

ConfigSectionPanel.propTypes = {
	editor: PropTypes.object.isRequired,
	selectedComponent: PropTypes.shape({
		model: PropTypes.object,
		el: PropTypes.object,
		editor: PropTypes.object
	}).isRequired,
	colorChangeable: PropTypes.bool,
	configurable: PropTypes.bool,
	layoutable: PropTypes.bool,
	backgroundEditable: PropTypes.bool
};

ConfigSectionPanel.defaultProps = {
	colorChangeable: true
};

export default ConfigSectionPanel;
